import React from 'react';

import { FormProvider } from 'react-hook-form';

import { ConfirmChangesModal } from 'components/common/confirmChangesModal/confirmChangesModal';

import { IAssignee } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import { Button } from 'components/DesignSystem/Button';
import { Modal } from 'components/DesignSystem/Modal';
import { AuditLog } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/AuditLog/AuditLog';
import { CardHeader } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/CardHeader/CardHeader';
import { useCreateRiskRegisterFormLogic } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/CreateRiskRegisterModal/components/CreateRiskRegisterForm/CreateRiskRegisterForm.logic';
import classes from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/CreateRiskRegisterModal/components/CreateRiskRegisterForm/CreateRiskRegisterForm.module.scss';
import { useCreateRiskRegisterFormProvider } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/CreateRiskRegisterModal/components/CreateRiskRegisterForm/CreateRiskRegisterForm.provider';
import { Details } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/Details/Details';
import { LinkedActivities } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/LinkedActivities/LinkedActivities';
import { Tabs } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/Tabs/Tabs';

interface Props {
    onClose: () => void;
    open: boolean;
}

export interface FormProps {
    id: null;
    name: string;
    status: 'NEW' | 'OPEN' | 'CLOSED';
    probability: 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH' | 'CERTAIN';
    impactType: 'RELATIVE' | 'ABSOLUTE';
    distribution: 'UNIFORM' | 'TRIANGLE' | 'BETA_PERT';
    distributionRange: {
        min: number;
        middle: number | null;
        max: number;
    };
    assignee: string;
    assigneeValue: ISelectOption<string> & { assignee: IAssignee };
    linkedActivities: number;
    description: string;
    contractId: number;
    linkOption: 'wbs' | 'activityCodes' | 'cluster' | 'specific' | 'segment' | 'all';
    activitiesByKey: {
        wbs?: number | null;
        activityCodes?: number[] | null;
        cluster?: number | null;
        specific?: string[] | null;
        segment?: number | null;
        all?: null;
    };
    tags: { name: string }[];
}

export const CreateRiskRegisterForm = ({ open, onClose }: Props) => {
    const { ui, api } = useCreateRiskRegisterFormProvider();
    const { logic } = useCreateRiskRegisterFormLogic({ api, ui, onClose });

    return (
        <Modal
            open={open}
            onClose={onClose}
            title={'Create New Risk'}
            actions={
                <>
                    <Button size={'large'} variant={'secondary'} onClick={logic.closeModalConfirm}>
                        Close
                    </Button>
                    <Button
                        size={'large'}
                        type={'submit'}
                        variant={'primary'}
                        onClick={logic.form.handleSubmit(logic.handleFormSubmit)}
                    >
                        Save
                    </Button>
                </>
            }
        >
            <FormProvider {...logic.form}>
                <form onSubmit={logic.form.handleSubmit(logic.handleFormSubmit)} className={classes.form}>
                    <CardHeader title={'Risk Item'} />
                    <Tabs activeTab={ui.state.activeTab} setActiveTab={ui.setters.setActiveTab} />
                    {ui.state.activeTab === 'details' && <Details />}
                    {ui.state.activeTab === 'linkedActivities' && <LinkedActivities />}
                    {ui.state.activeTab === 'auditLog' && <AuditLog />}
                </form>
                <ConfirmChangesModal
                    showModal={ui.state.showConfirmModal}
                    onHideModal={() => ui.setters.setShowConfirmModal(false)}
                    handleCloseWithoutSave={onClose}
                    handleSave={logic.form.handleSubmit(logic.handleFormSubmit)}
                    isLoading={api.createRisk.instance.isLoading}
                >
                    You made changes to this card. Would you like to save it?
                </ConfirmChangesModal>
            </FormProvider>
        </Modal>
    );
};
